import {BulkRewardResponse, BulkRewardStatus, PaymentCurrencyType, WalletEntityType} from "@irati/types"
import walletService from "@irati/wallet/graph/wallet"
import authentication from "@irati/auth"
import flexiPointService from "@irati/wallet/services/flexiRewards"
import RecognitionService from "@irati/recognition/services/Recognition"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import {parseRecognition} from "@irati/recognition/apollo/mutation/Recognition/Create"
import {DistributeRewardRecipient} from "../../enums/DistributeRewardTypes"
import {UseRewardsDistributionState} from "./hooks"

export const executeManualReward = async ({
  state,
  notifications
}: {
  state: UseRewardsDistributionState
  notifications: {
    assignedPoints: () => void
    sendReward: () => void
  }
}) => {
  const wallet = await walletService.get(authentication.user().uuid!, PaymentCurrencyType.Rp, WalletEntityType.Reward)

  const variables = {
    sourceId: wallet.uuid!,
    destinationUserIds: state.destinationUsers.map(({uuid}) => uuid!),
    amount: state.pointsPerUser * 100,
    message: state.message,
    budget: state.category?.uuid
      ? {
          categoryId: state.category?.uuid,
          useUnassigned: !!state.unassigned
        }
      : undefined
  }

  if (state.recipientType === DistributeRewardRecipient.MANAGER) {
    return flexiPointService
      .assignCredit(variables)
      .then(() => {
        notifications.assignedPoints()
        tracking.assignBudgetSuccess()
      })
      .catch((error) => {
        tracking.assignBudgetError(error)
        throw error
      })
  }

  return flexiPointService
    .rewardEmployees({
      ...variables,
      occasionId: state.rewardTemplate?.uuid,
      title: state.rewardTemplate ? state.rewardTemplateTitle : undefined
    })
    .then(async ({transferIds}) => {
      const {status} = await flexiPointService.getTransferOutcome(transferIds[0])

      if (status !== "COMPLETE") {
        throw Error(`Transfer failed status: ${status}`)
      }

      if (authentication.apps("recognition") && !state.rewardTemplate?.uuid) {
        const recognition = parseRecognition({walletTransferId: transferIds[0], ...state.recognition})
        await RecognitionService.create(recognition)
      }

      notifications.sendReward()
      tracking.rewardEmployeesSuccess(state.rewardTemplate?.name)
    })
    .catch((error) => {
      tracking.rewardEmployeesError(error)
      throw error
    })
}

export async function createBulkAndPollStatus(payload: UseRewardsDistributionState): Promise<BulkRewardResponse> {
  const {uuid, status} = await flexiPointService.createBulkReward({
    sourceId: authentication.user().uuid!,
    amount: payload.pointsPerUser * 100,
    currency: PaymentCurrencyType.Rp,
    businessId: authentication.userTenant().uuid!,
    emails: payload.userEmails,
    occasionId: payload.rewardTemplate?.uuid,
    title: payload.rewardTemplateTitle,
    budget: payload.category?.uuid
      ? {
          categoryId: payload.category.uuid,
          useUnassigned: !!payload.checkUnassigned
        }
      : undefined
  })

  if (status === BulkRewardStatus.Validating) {
    await pollTillValidated(uuid)
  }

  return {uuid, status}
}

async function pollTillValidated(uuid: string): Promise<BulkRewardStatus> {
  const {status} = await flexiPointService.bulkRewardStatus({
    uuid
  })
  if (status === BulkRewardStatus.AwaitingApproval) {
    return status
  }
  return new Promise((resolve, reject) => setTimeout(() => pollTillValidated(uuid).then(resolve).catch(reject), 2000))
}
